import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { LoaderIcon } from '@/components/ui/loader-icon';
import clsx from 'clsx';
import { ReactNode } from 'react';

export type InfoCardMaxItemsPerRow = 1 | 2 | 3 | 4;

function getResponsiveGridCols(max: InfoCardMaxItemsPerRow): string[] {
  const classes = {
    1: 'grid-cols-1-auto-fix',
    2: 'grid-cols-2-auto-fix',
    3: 'grid-cols-3-auto-fix',
    4: 'grid-cols-4-auto-fix',
  };

  switch (max) {
    case 1:
      return [classes[1]];
    case 2:
      return [`sm:${classes[1]}`, classes[2]];
    case 3:
      return [`sm:${classes[1]}`, `md:${classes[2]}`, classes[3]];
    case 4:
    default:
      return [`sm:${classes[1]}`, `md:${classes[2]}`, classes[4]];
  }
}

/**
 * Information card component with header and header actions
 */
export const InfoCard: React.FC<{
  headerText: ReactNode;
  /**
   * Optional subtitle displayed below the header text with a dark background.
   * Used to show additional context information (e.g., student name and grade).
   */
  headerSubtitle?: ReactNode;
  headerActions?: React.ReactNode;
  headerClass?: string;
  children: React.ReactNode;
  maxItemsPerRow?: InfoCardMaxItemsPerRow;
  contentGridClassName?: string;
  isLoading?: boolean;
  contentClass?: string;
  showRedDot?: boolean;
  footerContent?: React.ReactNode;
  footerActions?: React.ReactNode;
  footerClass?: string;
  className?: string;
  'data-testid'?: string;
}> = ({
  headerText,
  headerSubtitle,
  headerActions,
  headerClass,
  children,
  maxItemsPerRow = 4,
  contentGridClassName,
  isLoading = false,
  contentClass,
  showRedDot = true,
  footerContent,
  footerActions,
  footerClass,
  className,
  'data-testid': dataTestId,
}) => {
  const showHeader = Boolean(headerText) || Boolean(headerSubtitle) || Boolean(headerActions);

  return (
    <Card className={clsx('mb-5 shadow-md', className)}>
      {showHeader && (
        <CardHeader className={clsx('flex flex-row items-center justify-between space-y-0 pb-2', headerClass)}>
          <div className="flex flex-col gap-1">
            <CardTitle
              className="text-xl font-bold flex items-center"
              data-testid={dataTestId ? `${dataTestId}-header` : undefined}
            >
              {showRedDot && headerText && <div className="bg-[#EB404A] w-[6px] h-[24px] mr-2" />}
              {headerText}
            </CardTitle>
            {headerSubtitle && (
              <span className="bg-gray-800 text-white text-sm px-2 py-0.5 rounded w-fit">{headerSubtitle}</span>
            )}
          </div>
          <div className="flex items-start justify-end gap-x-2">{headerActions}</div>
        </CardHeader>
      )}
      <CardContent
        className={clsx(
          'grid auto-rows-min gap-y-5',
          showHeader ? 'pt-2' : 'pt-6',
          contentGridClassName ?? getResponsiveGridCols(maxItemsPerRow),
          contentClass,
        )}
      >
        {isLoading ? <LoaderIcon /> : children}
      </CardContent>
      {(footerContent || footerActions) && (
        <CardFooter className={clsx('flex flex-row items-center justify-between space-y-0 pt-2', footerClass)}>
          <div className="flex flex-col gap-1">{footerContent}</div>
          <div className="flex items-start justify-end gap-x-2">{footerActions}</div>
        </CardFooter>
      )}
    </Card>
  );
};
